import EventEmitter from 'eventemitter3';
import createSocket from 'socket.io-client';
import createDebug from 'debug';
import { AbortError } from '../Errors';
import { logErrorExternally, runAbortablePromise } from '@/functions';
import { Time, EventNames } from '@/constants';

/**
 * @typedef {import('../VesData').VesData} VesData
 * @typedef {{
 *   ETag: string;
 *   rawChunkNumber: number
 * }} CompletedRawChunk
 */

const debug = createDebug('durable:EagerVideoCompletionManager');

/**
 * @param {...*} args
 */
const logError = (...args) => {
  console.error('EagerVideoCompletionManager:', ...args);
};

const { TEN_SECONDS_IN_MS, THIRTY_SECONDS_IN_MS } = Time;
const GENERAL_TIMEOUT_IN_MS = TEN_SECONDS_IN_MS;
const FINISH_TIMEOUT_IN_MS = THIRTY_SECONDS_IN_MS;
const {
  ToServer: { START, RAW_CHUNK_READY_TO_EAGERLY_COMPLETE, FINISH },
  ToClient: { CONNECTED, ERROR },
} = EventNames.EagerVideoCompletionManager;
const { CONNECT, CONNECT_ERROR, DISCONNECT } = EventNames.SocketIO;

export class EagerVideoCompletionManager extends EventEmitter {
  /**
   *
   * @param {string} videoId
   * @param {string} recordingId
   * @param {string} recordingETag
   * @param {VesData} vesData
   * @param {string} containerFormat
   * @param {string} audioCodecRecorded
   * @param {string} videoCodecRecorded
   * @param {string} authToken
   */
  constructor(
    videoId,
    recordingId,
    recordingETag,
    vesData,
    containerFormat,
    audioCodecRecorded,
    videoCodecRecorded,
    authToken,
  ) {
    super();

    this.videoId = videoId;
    this.recordingId = recordingId;
    this.recordingETag = recordingETag;
    this.vesData = vesData;
    this.containerFormat = containerFormat;
    this.audioCodecRecorded = audioCodecRecorded;
    this.videoCodecRecorded = videoCodecRecorded;
    this.authToken = authToken;

    /** @type {CompletedRawChunk[]} */
    this.rawChunksAvailable = [];
    this.resetEagerCompletionState();
  }

  /**
   * @param {CompletedRawChunk} newChunk
   */
  handleReadyChunk(newChunk) {
    // Lazily open the socket to VES only when we have a chunk to send
    if (!this.socket) {
      this.openSocket();
    }

    const methodDebug = debug.extend('handleReadyChunk');
    methodDebug('A new chunk is ready:', newChunk);
    this.rawChunksAvailable.push(newChunk);

    this.eagerlyCompleteIfReady();
  }

  openSocket() {
    const { eagerlyCompleteVideoRecordingSocketUrl } = this.vesData;
    this.socket = createSocket(eagerlyCompleteVideoRecordingSocketUrl, {
      transports: ['websocket'],
      reconnection: false,
      auth: {
        token: this.authToken,
        env: this.vesData.requestEnv,
      },
    });

    this.socket.on(CONNECT, this.connectHandler.bind(this));
    this.socket.on(DISCONNECT, this.disconnectHandler.bind(this));
    this.socket.on(CONNECT_ERROR, this.connectErrorHandler.bind(this));
    this.socket.on(ERROR, this.errorHandler.bind(this));
  }

  async connectHandler() {
    const methodDebug = debug.extend('socket.on:connect');
    try {
      if (this.socket.recovered) {
        methodDebug('Socket reconnected to VES');
      } else {
        methodDebug('Socket connected to VES for the first time');
      }

      this.resetEagerCompletionState();

      this.emit(CONNECTED);

      const {
        videoId,
        recordingId,
        recordingETag,
        vesData: { environment },
        containerFormat,
        audioCodecRecorded,
        videoCodecRecorded,
      } = this;
      const startDetails = {
        videoId,
        recordingId,
        recordingETag,
        environment,
        containerFormat,
        audioCodecRecorded,
        videoCodecRecorded,
      };

      await this.emitWithAckAndTimeout(START, startDetails);

      this.readyToEagerlyCompleteChunks = true;

      this.eagerlyCompleteIfReady();
    } catch (error) {
      /**
       * If we have any error connecting or starting the eager completion, we want to error
       * externally so the uploader can retry or fall back to HTTP video completion. We want to
       * avoid any possibility of this class being in a state that's hung and prevents the video
       * from ever completing.
       */
      this.externallyEmitError('Error starting eager completion', error);
    }
  }

  resetEagerCompletionState() {
    this.readyToEagerlyCompleteChunks = false;
    // Valid chunks start at 1, so 0 means we haven't eagerly completed any chunks yet.
    this.lastRawChunkNumberEagerlyCompleted = 0;
    this.socketMessageInFlight = false;
    this.abortController = new AbortController();
    this.chunkMessageInProgressPromise = Promise.resolve();
    this.shouldFinishEagerCompletion = false;
  }

  disconnectHandler(reason) {
    const methodDebug = debug.extend('socket.on:disconnect');
    methodDebug('Socket disconnected from VES');
    if (!this.abortController.signal.aborted) {
      const abortError = new AbortError('Socket disconnected');
      this.abortController.abort(abortError);
    }

    // If disconnected, emit an error externally so uploader can retry or fall back
    const errorMessage = 'Socket disconnected';
    const error = new Error(errorMessage);
    this.externallyEmitError(errorMessage, error);
  }

  errorHandler(error) {
    this.externallyEmitError('Received socket error message', error);
  }

  /**
   * @param error
   */
  connectErrorHandler(error) {
    this.externallyEmitError('Encountered socket connect_error', error);
  }

  /**
   * Emit a message to the server with the given data and await a response from the server. Handle
   * aborting the message if we get disconnected and also add a timeout that the message must be
   * completed within or an error occurs, to prevent the message from hanging forever.
   *
   * @param {string} messageName the name of the message to emit
   * @param {unknown} messageData the data that will be sent with the message
   * @param {number} timeoutInMsToUse the timeout to use for the message, defaults to
   *   GENERAL_TIMEOUT_IN_MS
   * @returns {Promise<unknown>}
   */
  async emitWithAckAndTimeout(messageName, messageData, timeoutInMsToUse = GENERAL_TIMEOUT_IN_MS) {
    const methodDebug = debug.extend('emitWithAckAndTimeout');
    try {
      if (!this.socket || !this.socket.connected) {
        const notReadyMessage = 'Socket not ready yet to emit';
        methodDebug(notReadyMessage);
        throw new Error(notReadyMessage);
      }

      methodDebug('Emitting message', { eventName: messageName, messageData });
      this.socketMessageInFlight = true;
      const timeoutableMessagePromise = this.socket
        .timeout(timeoutInMsToUse)
        .emitWithAck(messageName, messageData);
      const response = await runAbortablePromise(
        timeoutableMessagePromise,
        this.abortController.signal,
      );
      return response;
    } catch (error) {
      logError('Error emitting message', error);
      throw error;
    } finally {
      this.socketMessageInFlight = false;
    }
  }

  /**
   * Determines if we are in a state where we can eagerly complete the next chunk and then does so.
   */
  eagerlyCompleteIfReady() {
    const methodDebug = debug.extend('eagerlyCompleteIfReady');
    if (!this.socket) {
      methodDebug('Socket not ready yet');
      return;
    }

    if (!this.socket.connected) {
      methodDebug('Socket not connected yet');
      return;
    }

    if (!this.readyToEagerlyCompleteChunks) {
      methodDebug('Not ready to eagerly complete chunks yet');
      return;
    }

    if (this.socketMessageInFlight) {
      methodDebug('Already sending a chunk');
      return;
    }

    if (this.shouldFinishEagerCompletion) {
      methodDebug('Should finish eager completion, so do not send any more chunks here');
      return;
    }

    if (
      this.rawChunksAvailable.some(
        (chunk) => chunk.rawChunkNumber > this.lastRawChunkNumberEagerlyCompleted,
      )
    ) {
      methodDebug('We have more chunks to eagerly complete');
      this.eagerlyCompleteNextChunk().catch((error) => {
        logError('Error eagerly completing next chunk when ready', error);
      });
    }
  }

  /**
   * Determines which chunk we should eagerly complete next and completes that specific chunk.
   *
   * @returns {Promise<void>}
   */
  async eagerlyCompleteNextChunk() {
    const methodDebug = debug.extend('eagerlyCompleteNextChunk');
    try {
      methodDebug('Awaiting previous message finish before handling next chunk...');
      await this.chunkMessageInProgressPromise;

      const chunkNumberToEagerlyComplete = this.lastRawChunkNumberEagerlyCompleted + 1;
      if (this.lastRawChunkNumberEagerlyCompleted === 0) {
        methodDebug('Have not eagerly completed any chunks yet, so eagerly combining chunk 1');
      } else {
        methodDebug(
          `Have already completed chunk ${this.lastRawChunkNumberEagerlyCompleted}, `,
          `so eagerly combining chunk ${chunkNumberToEagerlyComplete}`,
        );
      }

      const rawChunkToEagerlyComplete = this.rawChunksAvailable.find(
        (rawChunk) => rawChunk.rawChunkNumber === chunkNumberToEagerlyComplete,
      );
      if (!rawChunkToEagerlyComplete) {
        methodDebug('Next chunk to eagerly complete is not present yet', {
          rawChunkToEagerlyComplete,
        });
        return;
      }

      methodDebug('About to eagerly complete specific chunk', { rawChunkToEagerlyComplete });
      await this.eagerlyCompleteSpecificChunk(rawChunkToEagerlyComplete);

      this.eagerlyCompleteIfReady();
    } catch (error) {
      /**
       * If there is any error eagerly completing a chunk, we want to error externally so the
       * uploader can retry or fall back to HTTP video completion. We want to avoid any possibility
       * of this class being in a state that's hung and prevents the video from ever completing.
       */
      this.externallyEmitError('Error eagerly combining next chunk', error);
    }
  }

  /**
   * Creates a promise for sending the next chunk that can be awaited later.
   *
   * @param {CompletedRawChunk} chunk
   */
  async eagerlyCompleteSpecificChunk(chunk) {
    try {
      const methodDebug = debug.extend('eagerlyCompleteSpecificChunk');
      methodDebug('Chunk details', chunk);
      this.chunkMessageInProgressPromise = this.createChunkMessagePromise(chunk);
      await this.chunkMessageInProgressPromise;
    } catch (error) {
      logError('Error eagerly combining specific chunk', error);
      throw error;
    }
  }

  /**
   * The actual promise for sending the socket message that the chunk is ready to be eagerly
   * completed.
   *
   * @param {CompletedRawChunk} chunk
   * @returns {Promise<void>}
   */
  async createChunkMessagePromise(chunk) {
    try {
      const { ETag, rawChunkNumber } = chunk;

      await this.emitWithAckAndTimeout(RAW_CHUNK_READY_TO_EAGERLY_COMPLETE, {
        ETag,
        rawChunkNumber,
      });

      this.lastRawChunkNumberEagerlyCompleted = rawChunkNumber;
    } catch (error) {
      logError('Error sending chunk message');
      throw error;
    }
  }

  /**
   * Finish eager completion by processing all chunks that haven't been handled,
   * up to the lastCompletedRawChunkNumber that is passed in here.
   *
   * @param {number} lastCompletedRawChunkNumber
   * @returns {Promise<string>} videoUrl the url of the completed video
   */
  async finish(lastCompletedRawChunkNumber) {
    try {
      const methodDebug = debug.extend('finish');
      methodDebug('Starting...');

      this.shouldFinishEagerCompletion = true;

      if (!this.socket) {
        throw new Error('Socket not available');
      }

      if (!this.socket.connected) {
        throw new Error('Socket not connected');
      }

      if (!this.readyToEagerlyCompleteChunks) {
        throw new Error('Not ready to eagerly complete chunks');
      }

      methodDebug('Awaiting previous chunk message');
      await this.chunkMessageInProgressPromise;

      methodDebug('Completing all remaining chunks', {
        lastRawChunkNumberCompletedSoFar: this.lastRawChunkNumberEagerlyCompleted,
        lastRawChunkNumberToComplete: lastCompletedRawChunkNumber,
      });
      while (this.lastRawChunkNumberEagerlyCompleted < lastCompletedRawChunkNumber) {
        const chunkNumberToEagerlyComplete = this.lastRawChunkNumberEagerlyCompleted + 1;
        const rawChunkToEagerlyComplete = this.rawChunksAvailable.find(
          (rawChunk) => rawChunk.rawChunkNumber === chunkNumberToEagerlyComplete,
        );
        methodDebug('Sending chunk', { chunkNumberToEagerlyComplete, rawChunkToEagerlyComplete });
        if (!rawChunkToEagerlyComplete) {
          throw new Error(`Could not finish with chunk number ${chunkNumberToEagerlyComplete}`);
        }

        await this.eagerlyCompleteSpecificChunk(rawChunkToEagerlyComplete);
      }

      const videoUrl = await this.emitWithAckAndTimeout(
        FINISH,
        {
          lastCompletedRawChunkNumber,
        },
        FINISH_TIMEOUT_IN_MS,
      );

      methodDebug('Finished');
      return videoUrl;
    } catch (error) {
      logError('Error finishing eager completion', error);
      // If anything errors, throw so uploader can fall back to http video completion
      throw error;
    } finally {
      this.dispose();
    }
  }

  /**
   * Abort any messages in progress, and completely end socket communication.
   *
   * Since we are manually disconnecting, the socket will not retry opening. We assume that
   * dispose is only called in places where we would not expect reconnection to occur.
   */
  dispose() {
    try {
      if (!this.abortController.signal.aborted) {
        const abortError = new AbortError('Disposing of eager video completion');
        this.abortController.abort(abortError);
      }

      this.resetEagerCompletionState();

      const methodDebug = debug.extend('dispose');
      methodDebug('Starting...');
      if (!this.socket) {
        return;
      }

      this.socket.off(CONNECT);
      this.socket.off(CONNECT_ERROR);
      this.socket.off(DISCONNECT);
      this.socket.off(ERROR);

      if (this.socket.connected) {
        this.socket.disconnect();
      }

      this.socket = null;
    } catch (error) {
      this.externallyEmitError('Error disposing of eager completion', error);
    }
  }

  logErrorExternally(error) {
    try {
      logErrorExternally(
        'Unexpected error',
        error,
        'EagerVideoCompletionManager',
        { videoId: this.videoId },
        `videoId:${this.videoId}`,
      );
    } catch (loggingError) {
      logError('Error logging error externally', loggingError);
    }
  }

  /**
   * Externally log the error, dispose of the socket, and emit the error externally. So it can be
   * handled.
   *
   * @param {string} errorMessage
   * @param {unknown} error
   */
  externallyEmitError(errorMessage, error) {
    logError(errorMessage, error);
    this.logErrorExternally(error);
    this.dispose();
    this.emit(ERROR, error);
  }
}
